import { inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DpFieldCheckbox, DpFieldCustomLabel, DpFieldSelect, DpFormRow, DpModalForm, DpModalFormService, DpModalFormSize } from '@docpath/webcomponents';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { CloudService } from 'src/app/shared/services/cloud.service';

import { Alias } from '../../alias/models/alias';
import { Dge } from '../../engines/models/dge';
import { Notification, NotificationPartial } from '../models/notification';
import { NotificationOptions } from '../models/notification-options';
import { FailedJobsService } from './failed-jobs.service';
import { FailedJobsHttpService } from './failed-jobs-http.service';

@Injectable({
    providedIn: 'root'
})
export class FailedJobsFormService {
    readonly i18nPrefix = 'failed-jobs.form';


    private readonly modalFormService = inject(DpModalFormService);
    private readonly failedJobsHttpService = inject(FailedJobsHttpService);
    private readonly failedJobsService = inject(FailedJobsService);
    private readonly cloudService = inject(CloudService);

    initNotificationForm(notificationOptions: NotificationOptions, aliasIdSelected: number): DpModalForm {
        const { alias: aliasOptions } = notificationOptions;
        const { notifications } = notificationOptions;
        const { dges: dgesOptions } = notificationOptions;
        const alias: string[] = aliasOptions.map(aliasOption => aliasOption.alert_alias);

        const aliasesOptions = alias.map(option => ({
            value: option, text: option
        }));

        return new DpModalForm({
            header: `${this.i18nPrefix}.header.new-notification`,
            rows: [
                new DpFormRow([
                    new DpFieldSelect({
                        name: 'alias',
                        options: aliasesOptions,
                        required: true
                    })
                ]),
                new DpFormRow([
                    new DpFieldSelect({
                        name: 'dge',
                        options: this.getDgeOptions(aliasIdSelected, notifications, dgesOptions),
                        required: true
                    })
                ]),
                new DpFormRow([
                    new DpFieldCustomLabel({ name: 'alertBy', labelColumn: 4 }),
                    new DpFieldCheckbox({ name: 'sendEmail', column: 4, hidden: !this.sendAlertToEmail(aliasIdSelected, aliasOptions) }),
                    new DpFieldCheckbox({ name: 'sendSms', column: 2, hidden: !this.sendAlertToSms(aliasIdSelected, aliasOptions) })
                ])
            ],
            size: DpModalFormSize.Default,
            i18nPrefix: this.i18nPrefix
        });
    }

    openCreateNotificationForm(notificationOptions: NotificationOptions): void {
        const form = this.initNotificationForm(notificationOptions, notificationOptions.alias[0].id);
        form.header = `${this.i18nPrefix}.header.new-notification`;

        form.submit = (value, activeModal) => {
            this.postNotification(this.getNotificationToSend(value as Notification, notificationOptions.alias, notificationOptions.dges), activeModal);
        };

        const modalReference = this.modalFormService.open(form);

        modalReference.shown.subscribe(() => {
            this.setFieldsOptionsByAlias(form, notificationOptions);
            this.alertSendByControl(form);
            form.setValue({ alertBy: { label: `${this.i18nPrefix}.alert-by.label` }, sendEmail: false, sendSms: false });
        });
    }

    openEditNotificationForm(notification: Notification, notificationOptions: NotificationOptions): void {
        const form = this.initNotificationForm(notificationOptions, notification.aliasId);
        form.header = `${this.i18nPrefix}.header.edit-notification`;

        const modalReference = this.modalFormService.open(form);


        form.submit = (value, activeModal) => {
            const notificationValue = this.getNotificationToSend(value as Notification, notificationOptions.alias, notificationOptions.dges);
            notificationValue.id = notification.id;
            this.putNotification(notificationValue, activeModal);
        };

        modalReference.shown.subscribe(() => {
            this.disableEditionFields(form);
            this.alertSendByControl(form);
            this.handleDgeOptions(form, notification.dge);

            form.setValue(this.toNotificationFormValue(notification));
        });
    }

    private getNotificationToSend(notification: Notification, aliasOptions: Alias[], dgesOptions: Dge[]): NotificationPartial {
        const aliasId = aliasOptions.find(a => a.alert_alias === notification.alias)?.id ?? 0;
        const dgeId = dgesOptions.find(d => d.name === notification.dge)?.id ?? 0;

        return new NotificationPartial(aliasId, dgeId, notification.sendEmail === true, notification.sendSms === true);
    }

    private disableEditionFields(form: DpModalForm) {
        form.fields.forEach(field => {
            if (field.name === 'alias' || field.name === 'dge') {
                field.disabled = true;
            }
        });
    }

    private toNotificationFormValue(notificationValue: Notification): unknown {
        return { alias: notificationValue.alias, dge: notificationValue.dge, alertBy: { label: `${this.i18nPrefix}.alert-by.label` }, sendEmail: notificationValue.sendEmail, sendSms: notificationValue.sendSms };
    }

    private putNotification(notification: NotificationPartial, activeModal: NgbModalRef): void {
        this.failedJobsHttpService.editNotification(notification, () => {
            activeModal.close();
            this.failedJobsService.notifyFailedJobsChange();
        });
    }

    private postNotification(notification: NotificationPartial, activeModal: NgbModalRef): void {

        this.failedJobsHttpService.createNotification(notification, () => {
            activeModal.close();
            this.failedJobsService.notifyFailedJobsChange();
        });
    }

    private setFieldsOptionsByAlias(form: DpModalForm, notificationOptions: NotificationOptions): void {
        if (form.formGroup) {
            const aliasControl = form.formGroup.get('alias');
            this.updateFormByAlias(form, notificationOptions);

            aliasControl?.valueChanges.subscribe(() => {
                this.updateFormByAlias(form, notificationOptions);
            });
        }
    }

    private updateFormByAlias(form: DpModalForm, notificationOptions: NotificationOptions): void {
        const aliasControl = form.formGroup?.get('alias');
        const alias = aliasControl?.value;
        const aliasId = notificationOptions.alias.find(a => a.alert_alias === alias)?.id;

        // Reset DGE
        form.formGroup?.get('dge')?.setValue(null);

        const dgesOptionsSelect = this.getDgeOptions(
            aliasId,
            notificationOptions.notifications,
            notificationOptions.dges
        );

        this.setDgeOptions(form, dgesOptionsSelect);

        // Reset alert channels
        form.formGroup?.get('sendEmail')?.setValue(false);
        form.formGroup?.get('sendSms')?.setValue(false);

        const sendEmailField = form.getField('sendEmail') as DpFieldCheckbox;
        const sendSmsField = form.getField('sendSms') as DpFieldCheckbox;

        const sendEmailAlert = this.sendAlertToEmail(aliasId, notificationOptions.alias);
        const sendSMSAlert = this.sendAlertToSms(aliasId, notificationOptions.alias);

        sendEmailField.hidden = !sendEmailAlert;
        sendSmsField.hidden = !sendSMSAlert;
    }


    private getDgeOptions(aliasId: number | undefined, notifications: Notification[], dgesOptions: Dge[]) {
        return dgesOptions
            .filter(dge =>
                !notifications.some(
                    notification =>
                        notification.aliasId === aliasId &&
                        notification.dgeId === dge.id
                )
            )
            .map(dge => ({
                value: dge.name,
                text: dge.name
            }));
    }

    private setDgeOptions(form: DpModalForm, dgesOptionsSelect: { value: string; text: string; }[]): void {
        const dgeField = form.getField('dge') as DpFieldSelect;
        dgeField.setOptions(dgesOptionsSelect);

        const dgeControl = form.formGroup?.get('dge');

        if (dgesOptionsSelect.length > 0) {
            dgeControl?.setValue(dgesOptionsSelect[0].value);
        } else {
            dgeControl?.setValue(null);
            dgeControl?.markAsPristine();
            dgeControl?.markAsUntouched();
        }
    }

    private sendAlertToEmail(aliasId: number | undefined, aliasOptions: Alias[]): boolean {
        const aliasOption = aliasOptions.find(option => option.id === aliasId);

        return aliasOption?.email !== undefined && aliasOption?.email !== '';
    }

    private sendAlertToSms(aliasId: number | undefined, aliasOptions: Alias[]): boolean {
        const aliasOption = aliasOptions.find(option => option.id === aliasId);

        return aliasOption?.phone_number !== undefined && aliasOption?.phone_number !== '' && !this.cloudService.isModeCloud();
    }

    private alertSendByControl(form: DpModalForm): void {
        if (form.formGroup) {

            const onValidationSubmit = (formGroup: FormGroup) => {
                const emailControl = formGroup?.get('sendEmail');
                const smsControl = formGroup?.get('sendSms');
                const emailValue = emailControl?.value;
                const smsValue = smsControl?.value;

                if (!emailValue && !smsValue) {
                    form.buttons[1].enabled = false;
                    form.buttons[1].tooltip = 'failed-jobs.form.alert-by.validation';

                } else {
                    form.buttons[1].enabled = true;
                    form.buttons[1].tooltip = '';
                }
            };

            const emailControl = form.formGroup.get('sendEmail');
            const smsControl = form.formGroup.get('sendSms');

            emailControl?.valueChanges.subscribe(() => {
                form.buttons[1].onValidation = onValidationSubmit;
            });

            smsControl?.valueChanges.subscribe(() => {
                form.buttons[1].onValidation = onValidationSubmit;
            });
        }
    }

    private handleDgeOptions(form: DpModalForm, dgeValue: string): void {
        const dgeField = form.getField('dge') as DpFieldSelect;

        if (!dgeField || !dgeValue) {
            return;
        }

        const options = dgeField.options ?? [];

        const exists = options.some(option => option.value === dgeValue);

        if (!exists) {
            dgeField.setOptions([
                ...options,
                { value: dgeValue, text: dgeValue }
            ]);
        }
    }

}